/**
 * Application entry point. Initializes boot sequence, Medplum client,
 * router, and renders the app with providers.
 * @agentic connections
 */

// Styles
// NOTE: DO NOT REMOVE OR REORDER. THIS WILL BREAK MODULE STYLES.
//
// Mantine ships its CSS wrapped in `@layer mantine {}` via the `.layer.css`
// entrypoints. Layered rules always lose to unlayered rules in the CSS
// cascade, regardless of stylesheet source order. Because our app CSS modules
// (and `@benchmark/ui`) are unlayered, this guarantees app styles win over
// Mantine's base/component resets even when Vite emits chunk stylesheets in an
// order where a component chunk loads before the entry Mantine bundle (the
// production split-chunk cascade bug that broke PatientProfileCard on staging).
import "@mantine/core/styles.layer.css";
import "@medplum/react/styles.css";
import "@mantine/notifications/styles.layer.css";
// `@benchmark/ui` is consumed as a built library — its CSS-module classes are
// hashed at build time and bundled into `dist/styles.css`. The exports map
// (`./styles.css`) is the consumer-facing entry; without this side-effect
// import the connections app receives only the (hashed) class names on DOM
// nodes and none of the matching rules. Must come after Mantine core (whose
// CSS variables `@benchmark/ui` references) and before `./index.css` so the
// app's own overrides win.
import "@benchmark/ui/styles.css";
import "./index.css";

// Components
import { StrictMode } from "react";
import { RouterProvider } from "react-router";

// App Shell
import {
  initializeApp,
  initializeMedplumClient,
} from "@/app/boot/initializeApp";
import { initializePreloadErrorRecovery } from "@/app/boot/initializePreloadErrorRecovery";
import { connectUserErrorAuditEvents } from "@/app/boot/connectUserErrorAuditEvents";
import { globalEventEmitter } from "@benchmark/core/events";
import { AppProviders } from "@/app/providers";
import { router } from "@/app/routes/routerConfig";

// Utilities
import { createRoot } from "react-dom/client";

// Initialize application boot sequence
initializeApp();

// Initialize Medplum client
const medplum = initializeMedplumClient();

// Record user-visible errors as Medplum AuditEvents
connectUserErrorAuditEvents(globalEventEmitter, medplum);

// Recover from stale chunk preload failures after a redeploy
initializePreloadErrorRecovery();

/** Navigation function for MedplumProvider. @agentic connections */
const navigate = (path: string): Promise<void> => router.navigate(path);

/** Root DOM container for React. @agentic connections */
const container = document.getElementById("root") as HTMLDivElement;
/** React root for rendering. @agentic connections */
const root = createRoot(container);
root.render(
  <StrictMode>
    <AppProviders medplum={medplum} navigate={navigate}>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>,
);
